import type { DateKey } from '@arrow/game-engine';
import { useEffect, useState } from 'react';

import { type ChallengeResults, fetchChallengeResults } from '../api/client.ts';
import { feedbackLine } from '../game/challenge.ts';
import { dismissFeedback, feedbackChallenge } from '../platform/challenges.ts';
import { getDeviceId } from '../platform/identity.ts';
import { LimeTick } from './icons.tsx';

interface Props {
	todayKey: DateKey;
	/** Tests: the results fetch and the device id. */
	fetchResults?: (code: string, deviceId: string, signal?: AbortSignal) => Promise<ChallengeResults>;
	deviceId?: () => string;
	/** Heard when the line appears (Today keeps other notices off the page while it's up). */
	onShow?: () => void;
}

/**
 * Today's one quiet line about the challenge this device shared yesterday (or today):
 * "Yesterday's challenge: 3 friends played. Ana beat you by 0:21." Only once someone
 * has played; dismissible (remembered per challenge). Never blocks Today: it appears
 * when the results arrive, and nothing at all on any failure.
 */
export function ChallengeFeedback({ todayKey, fetchResults = fetchChallengeResults, deviceId = () => getDeviceId(), onShow }: Props) {
	const [line, setLine] = useState<{ code: string; lead: string; rest: string } | null>(null);

	useEffect(() => {
		const pick = feedbackChallenge(todayKey);
		if (!pick) return;
		const ctrl = new AbortController();
		fetchResults(pick.challenge.code, deviceId(), ctrl.signal).then(
			(results) => {
				const text = feedbackLine(results, pick.challenge.durationMs, pick.when);
				if (text && !ctrl.signal.aborted) setLine({ code: pick.challenge.code, ...text });
			},
			() => {
				// Quiet: no line.
			},
		);
		return () => ctrl.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [todayKey]);

	useEffect(() => {
		if (line) onShow?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [line]);

	if (!line) return null;
	return (
		<div className="fb" role="status" data-testid="challenge-feedback">
			<LimeTick className="fb-tick" />
			<p className="fb-text" title={`${line.lead} ${line.rest}`}>
				<b>{line.lead}</b> {line.rest}
			</p>
			<button
				type="button"
				className="fb-x"
				aria-label="Dismiss"
				onClick={() => {
					dismissFeedback(line.code);
					setLine(null);
				}}
			>
				<svg viewBox="0 0 24 24" aria-hidden="true">
					<path d="M6 6l12 12M18 6 6 18" />
				</svg>
			</button>
		</div>
	);
}
